import { updateFieldState } from '@gef-ui/redux-common/actions/fieldState';
import { updateGlobalErrors } from '@gef-ui/redux-common/actions/globalErrors';
import { globalErrors } from '@gef-ui/redux-common/constants';
import { hasAsyncValidations } from '@gef-ui/redux-common/selectors/fieldDescriptor';
import { getRegisteredField, isRegisteredFieldFieldPristine, isRegisteredFieldFieldValid } from '@gef-ui/redux-common/selectors/registeredFields';
import T from "ramda/es/T";
import always from "ramda/es/always";
import assoc from "ramda/es/assoc";
import compose from "ramda/es/compose";
import cond from "ramda/es/cond";
import curry from "ramda/es/curry";
import filter from "ramda/es/filter";
import flatten from "ramda/es/flatten";
import identity from "ramda/es/identity";
import ifElse from "ramda/es/ifElse";
import isEmpty from "ramda/es/isEmpty";
import map from "ramda/es/map";
import mapObjIndexed from "ramda/es/mapObjIndexed";
import mergeRight from "ramda/es/mergeRight";
import o from "ramda/es/o";
import prop from "ramda/es/prop";
import reject from "ramda/es/reject";
import toUpper from "ramda/es/toUpper";
import unless from "ramda/es/unless";
import values from "ramda/es/values";
import when from "ramda/es/when";
import zipObj from "ramda/es/zipObj";
import dotPath from "ramda-extension/es/dotPath";
import gtThanLength from "ramda-extension/es/gtThanLength";
import notEqual from "ramda-extension/es/notEqual";
import { batchActions } from 'redux-batched-actions';
import { VALID, severity, validationCombination } from '../../constants';
import { hasErrorValidations, hasRequiredValidations, hasWarnValidations } from '../../utils';
import { validateAsyncFields, validateFields, validateForm as validateFormCreator } from '../../validation/validations';
const { type, displayType, origin } = globalErrors;
const validateFieldAllDirty = validateFields(validationCombination.ALL);
const validateFieldAll = validateFields(validationCombination.ALL, validationCombination.ALL);
const validateFormAll = validateFormCreator(validationCombination.ALL);
const createGlobalError = (getFieldRegistered)=>({ fieldName, severity, errorMessage, metaInfo })=>({
            fieldName,
            section: dotPath('placement.section', getFieldRegistered(fieldName)),
            type: type.VALIDATION,
            displayType: displayType.FIELD,
            severity: toUpper(severity),
            origin: origin.FE,
            message: errorMessage,
            metaInfo
        });
const createGlobalErrors = o(map, createGlobalError);
const validCond = cond([
    [
        hasErrorValidations,
        always(severity.ERROR)
    ],
    [
        hasRequiredValidations,
        always(severity.ERROR)
    ],
    [
        hasWarnValidations,
        always(severity.WARNING)
    ],
    [
        T,
        always(VALID)
    ]
]);
const getActualValidationGuid = (formName, fieldName, getState)=>getRegisteredField(formName)(getState())(fieldName).state.validationGuid;
const solveResult = compose(map, ifElse(gtThanLength(0)));
const hasErrors = o(notEqual(VALID), prop('valid'));
const validate = (formName, validateFn)=>(dispatch, getState)=>{
        const state = getState();
        const regFields = getRegisteredField(formName)(state);
        const validationResult = validateFn(formName, state);
        const globalErrors = solveResult(identity, createGlobalErrors(regFields))(validationResult);
        const fieldState = map((res)=>{
            const validState = {
                valid: validCond(res)
            };
            return when(hasErrors, mergeRight({
                pristine: false
            }))(validState);
        }, validationResult);
        dispatch(batchActions([
            updateGlobalErrors(formName)(globalErrors),
            updateFieldState(formName)(fieldState)
        ]));
    };
export const validateFieldsAll = curry((formName, fieldsToValidate)=>validate(formName, validateFieldAll(fieldsToValidate)));
const validateAsync = (validatePristine)=>curry((formName, fieldsToValidate)=>async (dispatch, getState)=>{
            const state = getState();
            const filterAsyncFields = filter(hasAsyncValidations(formName)(state));
            const rejectPristineWhenShould = when(always(!validatePristine), reject(isRegisteredFieldFieldPristine(formName)(state)));
            const filteredFields = o(rejectPristineWhenShould, filterAsyncFields)(fieldsToValidate);
            if (isEmpty(filteredFields)) {
                return Promise.resolve();
            }
            await dispatch(validateFieldsAll(formName, filteredFields));
            const validFields = o(flatten, filter(isRegisteredFieldFieldValid(formName)(getState())))(filteredFields);
            if (isEmpty(validFields)) {
                return Promise.resolve();
            }
            const fieldsWithGuids = zipObj(validFields, map(()=>({
                    validationGuid: crypto.randomUUID()
                }), validFields));
            dispatch(updateFieldState(formName)(map(assoc('asyncValidationInProgress', true), fieldsWithGuids)));
            const regFields = getRegisteredField(formName)(state);
            const validationResult = validateAsyncFields(validatePristine)(fieldsWithGuids, formName, state, dispatch);
            const promises = mapObjIndexed((res, fieldName)=>res.then(({ msgs, validationGuid })=>{
                    if (getActualValidationGuid(formName, fieldName, getState) === validationGuid) {
                        const globalErrors = unless(gtThanLength(0), createGlobalErrors(regFields))(msgs);
                        dispatch(batchActions([
                            updateGlobalErrors(formName)({
                                [fieldName]: globalErrors
                            }),
                            updateFieldState(formName)({
                                [fieldName]: {
                                    valid: validCond(msgs),
                                    asyncValidationInProgress: false
                                }
                            })
                        ]));
                    }
                    return 'done';
                }).catch(({ originalError, validationGuid })=>{
                    if (getActualValidationGuid(formName, fieldName, getState) === validationGuid) {
                        dispatch(updateFieldState(formName)({
                            [fieldName]: {
                                asyncValidationInProgress: false
                            }
                        }));
                    }
                    return Promise.reject(originalError);
                }))(validationResult);
            await Promise.all(values(promises));
            return Promise.resolve();
        });
export const validateField = curry((formName, fieldsToValidate)=>validate(formName, validateFieldAllDirty(fieldsToValidate)));
export const validateDirtyFieldsAsync = validateAsync(false);
export const validateAllFieldsAsync = validateAsync(true);
export const validateForm = (formName)=>validate(formName, validateFormAll);
